import {
	forwardRef,
	useRef,
	useState,
	useEffect,
	useImperativeHandle,
	useMemo,
} from 'react'
import {cx, tv} from '@local/utils/cx'
import {VariantProps} from 'tailwind-variants'

const dropdownStyles = tv({
	slots: {
		base: cx(
			'bg-transparent',
			'border border-grey-1 rounded-xs',
			'h-44',
			'text-neutral-100',
			'cursor-pointer',
			'relative',
			'col-span-2',
			'bg-transparent flex items-center  px-10 h-48 text-12',
			'rounded-xxs border border-grey-2 peer-focus-visible:border-grey-1',
			'peer-checked:border-white transition-colors duration-200 ease-quart-out hover:cursor-pointer',
			'peer-invalid:border-red',
		),
		optionLabel: cx(
			'text-12',

		),
		option: cx(
			'hover:bg-grey-5 px-10 py-10 hover:text-white-off col-span-2',
		),
		optionsContainer: cx(
			'absolute',
			'w-full',
			'left-0',
			'mt-5',
			'z-10',
			'max-h-204',
			'flex flex-col',
			'overflow-hidden',
			'border',
			'rounded-xs',
			'top-full',
			'bg-grey-4',
			'animation-opacity ease-quart-out duration-5300',
		),
		searchInput: cx(
			'w-full shrink-0 border-0 border-b border-grey-2 bg-grey-4 px-10 py-10 text-12 text-neutral-100',
			'placeholder:text-neutral-300 outline-none focus:border-grey-1 focus:ring-0',
		),
		optionsScroll: cx(
			'min-h-0 flex-1 overflow-auto',
			'max-h-204',
		),
	},
	variants: {
		open: {
			true: {
				optionsContainer: 'block',
				base: 'border-green-500',
			},
			false: {
				optionsContainer: 'hidden',
			},
		},
		searchable: {
			true: {
				optionsContainer: 'max-h-388',
				optionsScroll: 'max-h-340',
			},
			false: {},
		},
	},
})

type DropdownVariantProps = VariantProps<typeof dropdownStyles>;

export interface Option {
	label: string;
	value: string;
	keywords?: string;
}

function normalizeForSearch (s: string) {
	return s.toLowerCase().replace(/\s+/g, ' ').trim()
}

function optionMatchesQuery (option: Option, query: string) {
	if (!query) {
		return true
	}
	const nq = normalizeForSearch(query)
	const haystack = normalizeForSearch(
		option.keywords ??
			[option.label, option.value].filter(Boolean).join(' '),
	)
	return haystack.includes(nq)
}

interface DropdownProps extends DropdownVariantProps {
	id: string;
	name: string;
	options: Option[];
	placeholder?: string;
	dropdownClassName?: string,
	defaultValue?: Option,
	onSelect?: (value: any) => void;
	searchable?: boolean;
	searchPlaceholder?: string;
}

export const Dropdown = forwardRef((
	{
		id,
		name,
		options,
		dropdownClassName,
		placeholder,
		defaultValue,
		onSelect,
		searchable = false,
		searchPlaceholder = 'Search',
		...props
	}: DropdownProps,
	ref,
) => {
	const [isOpen, setIsOpen] = useState(false)
	const [selected, setSelected] = useState<Option | undefined>(defaultValue)
	const [searchQuery, setSearchQuery] = useState('')
	const searchInputRef = useRef<HTMLInputElement>(null)

	const dropdownRef = useRef<HTMLDivElement>(null)

	const filteredOptions = useMemo(() => {
		if (!searchable || !searchQuery.trim()) {
			return options
		}
		return options.filter((opt) => optionMatchesQuery(opt, searchQuery))
	}, [options, searchQuery, searchable])

	const handleSelect = (option: Option) => {
		setSelected(option)
		setIsOpen(false)
		onSelect?.(option.value)
	}

	useEffect(() => {
		if (!isOpen) {
			setSearchQuery('')
		}
	}, [isOpen])

	useEffect(() => {
		if (isOpen && searchable && searchInputRef.current) {
			searchInputRef.current.focus()
		}
	}, [isOpen, searchable])

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsOpen(false)
			}
		}

		const handleScroll = (event: Event) => {
			if (isOpen) {
				event.stopPropagation()
			}
		}

		document.addEventListener('mousedown', handleClickOutside)
		document.addEventListener('wheel', handleScroll, {passive: false})

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
			document.removeEventListener('wheel', handleScroll)
		}
	}, [isOpen, dropdownRef])

	useImperativeHandle(ref, () => ({
		getSelected: () => selected,
	}))

	const {base: baseClassName, optionLabel: optionLabelClassname, optionsContainer: optionsContainerClassName, option: optionClassName, searchInput: searchInputClassName, optionsScroll: optionsScrollClassName} = dropdownStyles({
		open: isOpen,
		searchable,
	})

	const optionsContainerDynamicStyles = cx(
		optionsContainerClassName(),
		'transition-opacity ease-linear duration-1300',
		isOpen ? 'opacity-100 visible' : 'opacity-0 invisible absolute')

	const toggleOpen = () => setIsOpen((o) => !o)

	return (
		<div ref={dropdownRef} className={cx(baseClassName(), dropdownClassName)} onClick={toggleOpen} {...props}>
			{selected?.value &&
				<label
					htmlFor={id || name}
					className={cx(
						'absolute top-0 ml-5 bg-grey-4 px-5 -translate-y-1/2 z-2 origin-left peer-focus:top-0 pointer-events-none left-0 mr-1',
						'top-0',

					)}>
					{placeholder}
				</label>}
			<input readOnly hidden id={id} name={name} value={selected?.value ?? ''} />
			<span className={cx(optionLabelClassname())}>{selected?.label || placeholder}</span>
			<div
				className={cx(optionsContainerDynamicStyles)}
				onClick={(e) => e.stopPropagation()}
				onMouseDown={(e) => e.stopPropagation()}>
				{searchable && (
					<div
						className='sticky top-0 z-1 shrink-0 border-b border-grey-2 bg-grey-4'
						onClick={(e) => e.stopPropagation()}
						onMouseDown={(e) => e.stopPropagation()}
						onKeyDown={(e) => e.stopPropagation()}>
						<input
							ref={searchInputRef}
							type='search'
							autoComplete='off'
							placeholder={searchPlaceholder}
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							className={searchInputClassName()}
							onClick={(e) => e.stopPropagation()} />
					</div>
				)}
				<div className={optionsScrollClassName()}>
					{filteredOptions.length === 0 && (
						<div className={cx(optionClassName(), 'cursor-default hover:bg-transparent hover:text-neutral-100')}>
							No matches
						</div>
					)}
					{filteredOptions.map((option, index) => (
						<div
							key={`${option.value}-${index}`}
							className={cx(optionClassName())}
							onClick={(e) => {
								e.stopPropagation()
								handleSelect(option)
							}}>
							{option.label}
						</div>
					))}
				</div>
			</div>
		</div>
	)
})
